import * as React from "react";
import { useEffect, useState, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { axiosRequest, RequestType } from "../../utils/network";
import { AxiosResponse } from "axios";

interface Asset {
  code: string;
  name: string;
  icon_url: string;
}

interface Wallet {
  id: string;
  asset: Asset;
  balance: string;
  base_balance: number;
}

interface LedgerEntry {
  id: string;
  amount: string;
  category: string;
  createdAtIso: string;
  originId: string;
}

interface Distribution {
  id: string;
  state: string;
  vesting_date: string;
  amount: {
    cents: number;
    currency_iso: string;
  };
}

const useStyles = makeStyles(() => ({
  page: {
    backgroundColor: "#F9FAFB",
    fontFamily: "Inter, sans-serif",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    maxWidth: "100%",
    padding: "0 24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    boxSizing: "border-box",
  },
  balanceHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  balanceLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: "#6B7280",
    marginBottom: -12,
  },
  h1: {
    fontSize: "65px",
    fontWeight: 800,
    color: "#111827",
  },
  actionRow: {
    display: "flex",
    gap: "12px",
  },
  actionBtn: {
    backgroundColor: "#111827",
    color: "#FFF",
    padding: "10px 18px",
    borderRadius: "24px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    border: "none",
    minWidth: "120px",
    '&:hover': {
      backgroundColor: "#1F2937"
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },

  lightBtn: {
    backgroundColor: "#F3F4F6",
    color: "#111827",
    padding: "10px 18px",
    borderRadius: "24px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    border: "none",
    minWidth: "120px",
    '&:hover': {
      backgroundColor: "#111827",
      color: "#FFFFFF",
      border: "1px solid #111827",
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },
  searchBox: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #D1D5DB",
    fontSize: 14,
    width: "100%",
    maxWidth: "400px",
  },
  assetScrollWrapper: {
    overflow: "hidden",
    display: "grid",
  },
  assetScroll: {
    display: "flex",
    gap: 16,
    overflowX: "auto",
    paddingTop: "12px",
    paddingBottom: "12px",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    boxSizing: "border-box",
  },
  assetCard: {
    minWidth: 160,
    maxWidth: 180,
    scrollSnapAlign: "start",
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    cursor: "pointer",
    border: "1px solid #E5E7EB",
    "&.selected": {
      border: "2px solid #111827",
      boxShadow: "0 0 0 2px rgba(17,24,39,0.1)",
    },
  },
  assetIcon: {
    width: 48,
    height: 48,
    backgroundColor: "#E5E7EB",
    borderRadius: "12px",
    marginBottom: 12,
  },
  assetSymbol: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  assetBalance: {
    fontSize: 14,
    color: "#6B7280",
  },
  assetDetailSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "20px",
  },
  assetName: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    marginBottom: "16px",
  },
  assetMetricsRow: {
    display: "flex",
    justifyContent: "flex-start",
    gap: "65px",
    marginBottom: "24px",
  },
  metricLabel: {
    fontSize: "18px",
    color: "#6B7280",
    marginBottom: "8px",
  },
  metricValue: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
  },
  actionButtonsRow: {
    display: "flex",
    gap: "12px",
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 600,
    color: "#6B7280",
    paddingLeft: 4,
  },
  txList: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    gap: 16,
  },
  txRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    alignItems: "center",
    fontSize: 14,
    color: "#374151",
  },
  txCategory: {
    fontWeight: 600,
    textTransform: "capitalize",
  },
  txAmountPositive: {
    justifySelf: "center",
    fontWeight: 600,
    color: "#10B981", // green
  },
  txAmountNegative: {
    justifySelf: "center",
    fontWeight: 600,
    color: "#EF4444", // red
  },
  txAmount: {
    justifySelf: "center",
    fontWeight: 600,
    color: "#10B981",
  },

  txDate: {
    justifySelf: "end",
    color: "#9CA3AF",
    fontSize: 13,
  },

  shimmer: {
    animation: "$shimmer 1.5s infinite linear",
    background: "linear-gradient(to right, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
    backgroundSize: "200% 100%",
  },
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "-200% 0" },
    "100%": { backgroundPosition: "200% 0" },
  },
  shimmerBox: {
    borderRadius: "8px",
    backgroundColor: "#F3F4F6",
  },
  balanceShimmer: {
    height: "72px",
    width: "180px",
    borderRadius: "8px",
  },
  assetCardShimmer: {
    height: 140,
    width: 160,
    borderRadius: "12px",
    backgroundColor: "#F3F4F6",
    flexShrink: 0,
  },
  txRowShimmer: {
    height: 20,
    borderRadius: 6,
    backgroundColor: "#F3F4F6",
    width: "100%",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    marginTop: "24px",
    marginBottom: "12px",
  },

  notificationBanner: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "16px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginTop: "16px",
  },

  bannerContent: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#374151",
  },

  bannerLink: {
    color: "#374151",
    textDecoration: "underline",
    marginLeft: "4px",
    fontWeight: 500,
  },

  bannerTextContent: {
    flex: 1,
    marginLeft: "12px",
  },

  bannerTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
  },

  bannerSubtitle: {
    fontSize: "14px",
    color: "#6B7280",
  },

  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
  },

  assetIconSmall: {
    width: "36px",
    height: "36px",
  },

  rewardsCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "16px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },

  rewardsCardContent: {
    display: "flex",
    alignItems: "center",
  },

  rewardsTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
  },

  rewardsBalance: {
    fontSize: "14px",
    color: "#6B7280",
    textTransform: "uppercase",
  },

  offerCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "16px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },

  offerCardContent: {
    display: "flex",
    alignItems: "center",
  },

  offerTextContent: {
    flex: 1,
    marginLeft: "12px",
  },

  offerTitle: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#111827",
  },

  offerSubtitle: {
    fontSize: "14px",
    color: "#6B7280",
  },
  distributionSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "20px",
  },
  distributionHeader: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    marginBottom: "16px",
    borderBottom: "1px solid #E5E7EB",
    paddingBottom: "8px",
  },
  distributionRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    padding: "12px 0",
    borderBottom: "1px solid #F3F4F6",
  },
  distributionDate: {
    fontSize: "14px",
    color: "#6B7280",
  },
  distributionAmount: {
    fontSize: "14px",
    color: "#111827",
  },
  distributionStatus: {
    fontSize: "14px",
    fontWeight: 600,
    textAlign: "right",
  },
  distributionStatusCompleted: {
    color: "#10B981",
  },
  distributionStatusPending: {
    color: "#F59E0B",
  },
  distributionEmpty: {
    padding: "16px 0",
    color: "#6B7280",
    fontSize: "14px",
    textAlign: "center",
  },
}));

const EARNING_RATES = {
  "USDE": "Up to 7% APY",
  "SOL": "Up to 5.5% APY",
  "WEETH": "3.8% APR",
  "WSTETH": "2.8% APR",
  "MINA": "Up to 9.74% APY",
  "AXL": "Up to 7% APY",
  "CSPR": "Up to 6.8% APY",
  "SUI": "Up to 2.5% APY",
};

const WalletPage: React.FC = () => {
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevSearchTerm = useRef("");
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [signedLedgerEntries, setSignedLedgerEntries] = useState<LedgerEntry[]>([]);
  const [totalBalance, setTotalBalance] = useState<string | null>("$0");
  const [distributionSchedule, setDistributionSchedule] = useState<Distribution[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [isLoadingBalances, setIsLoadingBalances] = useState(true);
  const [isLoadingLedger, setIsLoadingLedger] = useState(true);
  const [isLoadingTotal, setIsLoadingTotal] = useState(true);
  const [isLoadingDistribution, setIsLoadingDistribution] = useState(false);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const toCapitalizedWords = (input: string) =>
    input.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    const fetchWallets = async () => {
      setIsLoadingBalances(true);
      setIsLoadingTotal(true);
      try {
        const response = await axiosRequest("/fast_wallet/wallet_data", {}, RequestType.GET) as AxiosResponse<Wallet[]>;
        const wallets = response.data;
        wallets.forEach(wallet => {
          if (!wallet.asset.name?.trim()) {
            wallet.asset.name = wallet.asset.code;
          }
        });
        wallets.sort((a, b) => {
          if (a.balance === "0" && b.balance !== "0") return 1;
          if (b.balance === "0" && a.balance !== "0") return -1;
          return b.base_balance - a.base_balance;
        });
        setWallets(wallets);
        setSelectedWallet(wallets[0]);

        const calculatedTotal = wallets.reduce((sum, wallet) => sum + wallet.base_balance, 0);
        setTotalBalance(`${calculatedTotal.toFixed(2)} USD`);
      } catch (err) {
      } finally {
        setIsLoadingBalances(false);
        setIsLoadingTotal(false);
      }
    };

    const fetchLedgerEntries = async () => {
      setIsLoadingLedger(true);
      try {
        const response = await axiosRequest("/fast_wallet/ledger_entries", {}, RequestType.GET) as AxiosResponse<LedgerEntry[]>;
        setLedgerEntries(response.data);
      } catch (err) {
        setLedgerEntries([]);
      } finally {
        setIsLoadingLedger(false);
      }
    };

    fetchWallets();
    fetchLedgerEntries();
  }, []);

  const fetchDistributionSchedule = async (assetSymbol: string) => {
    setIsLoadingDistribution(true);
    try {
      const response = await axiosRequest(
        "/fast_wallet/distribution_schedule",
        { asset_symbol: assetSymbol },
        RequestType.GET
      ) as AxiosResponse<Distribution[]>;
      setDistributionSchedule(response.data);
    } catch (err) {
      setDistributionSchedule([]);
    } finally {
      setIsLoadingDistribution(false);
    }
  };

  useEffect(() => {
    if (prevSearchTerm.current && !searchTerm && scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
    prevSearchTerm.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    if (selectedWallet) {
      fetchDistributionSchedule(selectedWallet.asset.code);
    }
  }, [selectedWallet]);

  const filteredWallets = React.useMemo(() => {
    const newWallets = [...wallets]
      .filter((wallet) =>
        wallet.asset.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wallet.asset.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    setSelectedWallet(newWallets[0] || null);
    return newWallets;
  }, [wallets, searchTerm]);

  useEffect(() => {
    const walletIds = new Set(wallets.map(wallet => wallet.id));
    const signedLedgerEntries = ledgerEntries.map(entry => {
      if (walletIds.has(entry.originId)) {
        return { ...entry, amount: `-${entry.amount}` };
      } else {
        return { ...entry, amount: `+${entry.amount}` };
      }
    })
    setSignedLedgerEntries(signedLedgerEntries);
  }, [wallets, ledgerEntries]);

  const filteredLedgerEntries = React.useMemo(() => {
    if (!selectedWallet) return signedLedgerEntries;
    return signedLedgerEntries.filter(entry =>
      entry.amount.endsWith(` ${selectedWallet.asset.code}`)
    );
  }, [signedLedgerEntries, selectedWallet]);

  const handleDeposit = () => {
    if (selectedWallet) {
      window.location.href = `/wallet_redirect/${selectedWallet.asset.code}/deposit`;
    }
  };

  const handleWithdraw = () => {
    if (selectedWallet) {
      window.location.href = `/wallet_redirect/${selectedWallet.asset.code}/withdraw`;
    }
  };

  const handleTrade = () => {
    if (selectedWallet) {
      window.location.href = `/wallet_redirect/${selectedWallet.asset.code}/trade`;
    }
  };

  const selectedSymbol = selectedWallet?.asset.code || "";

  const DistributionSchedule = () => {
    if (isLoadingDistribution) {
      return (
        <div className={classes.distributionSection}>
          <div className={classes.distributionHeader}>Distribution Schedule</div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`${classes.shimmer} ${classes.txRowShimmer}`} style={{ marginBottom: '10px' }} />
          ))}
        </div>
      );
    }

    if (!distributionSchedule.length) {
      return null;
    }

    return (
      <>
        <div className={classes.assetName}>Distribution Schedule</div>
        <div className={classes.distributionSection}>
          {distributionSchedule.length > 0 ? (
            distributionSchedule.map((distribution) => (
              <div key={distribution.id} className={classes.distributionRow}>
                <div className={classes.distributionDate}>
                  {formatDate(distribution.vesting_date)}
                </div>
                <div className={classes.distributionAmount}>
                  {distribution.amount.cents} {distribution.amount.currency_iso}
                </div>
                <div className={`
                ${classes.distributionStatus} 
                ${distribution.state === "completed"
                    ? classes.distributionStatusCompleted
                    : classes.distributionStatusPending}
              `}>
                  {distribution.state === "completed" ? "Completed" : "Pending"}
                </div>
              </div>
            ))
          ) : (
            <div className={classes.distributionEmpty}>
              No distribution schedule available
            </div>
          )}
        </div>
      </>);
  };

  const RewardsBanner = ({ assetCode, assetIconUrl }) => {
    const classes = useStyles();

    if (assetCode === "SOL") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <i className="icon-info-circled-alt" style={{ marginRight: 8 }} />
            <span>
              SOL automatically earns up to 5.5% APY when deposited into your CoinList Wallet.
              <a
                href="https://coinlist.co/help/information-on-sol-staking"
                className={classes.bannerLink}
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "USDE") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <i className="icon-info-circled-alt" style={{ marginRight: 8 }} />
            <span>
              Rewards for holding USDe will be automatically distributed in your wallet every Friday.
              <a
                href="https://blog.coinlist.co/earn-6-apy-with-ethenas-usde/"
                className={classes.bannerLink}
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "KING") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <i className="icon-info-circled-alt" style={{ marginRight: 8 }} />
            <span>
              KING Tokens will be automatically distributed in the coming weeks from holding weETH.
              <a
                href="https://blog.coinlist.co/introducing-weeth-wsteth-staking-on-coinlist/"
                className={classes.bannerLink}
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "LBTC") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <div className={classes.iconWrapper}>
              <img
                src={assetIconUrl}
                alt="LBTC"
                className={classes.assetIconSmall}
              />
            </div>
            <div className={classes.bannerTextContent}>
              <div className={classes.bannerTitle}>LBTC</div>
              <div className={classes.bannerSubtitle}>
                Earn Lombard Lux and Babylon points when you hodl LBTC
              </div>
            </div>
            <a href="/lombard" className={classes.lightBtn}>
              Learn More
            </a>
          </div>
        </div>
      );
    }

    return null;
  };

  const OffersSection = ({ assetCode, assetIconUrl, wallets, onSelectWallet }) => {
    const classes = useStyles();

    if (assetCode === "WEETH") {
      // Find the KING wallet if it exists
      const kingWallet = wallets.find(w => w.asset.code === "KING");

      return (
        <>
          <h3 className={classes.sectionTitle}>Rewards</h3>
          <div className={classes.rewardsCard} onClick={() => kingWallet && onSelectWallet(kingWallet)}>
            <div className={classes.rewardsCardContent}>
              <img
                src={kingWallet ? kingWallet.asset.icon_url : "/assets/tokens/KING.svg"}
                alt="KING"
                className={classes.assetIcon}
              />
              <div>
                <div className={classes.rewardsTitle}>KING Tokens</div>
                <div className={classes.rewardsBalance}>
                  {kingWallet ? `${kingWallet.balance} KING` : "0 KING"}
                </div>
              </div>
            </div>
          </div>

          <h3 className={classes.sectionTitle}>Offers</h3>
          <div className={classes.offerCard}>
            <div className={classes.offerCardContent}>
              <img
                src={assetIconUrl}
                alt="WEETH"
                className={classes.assetIcon}
              />
              <div className={classes.offerTextContent}>
                <div className={classes.offerTitle}>Introducing weETH Staking</div>
                <div className={classes.offerSubtitle}>
                  Earn KING Protocol Tokens and yield when you hold weETH
                </div>
              </div>
              <a
                href="https://blog.coinlist.co/introducing-weeth-wsteth-staking-on-coinlist/"
                className={classes.lightBtn}
              >
                Learn More
              </a>
            </div>
          </div>
        </>
      );
    }

    if (assetCode === "KING") {
      const weethWallet = wallets.find(w => w.asset.code === "WEETH");

      return (
        <>
          <h3 className={classes.sectionTitle}>Offers</h3>
          <div className={classes.offerCard}>
            <div className={classes.offerCardContent}>
              <img
                src={weethWallet ? weethWallet.asset.icon_url : "/assets/tokens/WEETH.svg"}
                alt="WEETH"
                className={classes.assetIcon}
              />
              <div className={classes.offerTextContent}>
                <div className={classes.offerTitle}>Introducing weETH Staking</div>
                <div className={classes.offerSubtitle}>
                  Earn KING Protocol Tokens and yield when you hold weETH
                </div>
              </div>
              <a
                href="https://blog.coinlist.co/introducing-weeth-wsteth-staking-on-coinlist/"
                className={classes.lightBtn}
              >
                Learn More
              </a>
            </div>
          </div>
        </>
      );
    }

    if (assetCode === "USDE") {
      return (
        <>
          <h3 className={classes.sectionTitle}>Offers</h3>
          <div className={classes.offerCard}>
            <div className={classes.offerCardContent}>
              <img
                src={assetIconUrl}
                alt="USDE"
                className={classes.assetIcon}
              />
              <div className={classes.offerTextContent}>
                <div className={classes.offerTitle}>Hold Ethena USDe, Earn 7% APY</div>
                <div className={classes.offerSubtitle}>
                  Hold USDe on CoinList and earn up to 7% APY. No staking, no lockups.
                </div>
              </div>
              <a
                href="https://blog.coinlist.co/earn-6-apy-with-ethenas-usde/"
                className={classes.lightBtn}
              >
                Learn More
              </a>
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className={classes.page}>
      <div className={classes.container}>
        <div className={classes.balanceHeaderRow}>
          <div>
            {!isLoadingTotal && (
              <div className={classes.balanceLabel}>Your balance</div>
            )}
            {isLoadingTotal ? (
              <div className={`${classes.shimmer} ${classes.shimmerBox} ${classes.balanceShimmer}`} />
            ) : (
              <div className={classes.h1}>{totalBalance}</div>
            )}
          </div>
        </div>


        <input
          type="text"
          placeholder="Search assets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchBox}
        />

        <div className={classes.assetScrollWrapper}>
          <div className={classes.assetScroll} ref={scrollRef}>
            {isLoadingBalances
              ? [...Array(10)].map((_, i) => (
                <div key={i} className={`${classes.shimmer} ${classes.assetCardShimmer}`} />
              ))
              : filteredWallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className={`${classes.assetCard} ${selectedSymbol === wallet.asset.code ? "selected" : ""}`}
                  onClick={() => setSelectedWallet(wallet)}
                >
                  <img className={classes.assetIcon} src={wallet.asset.icon_url} alt={wallet.asset.code} />
                  <div className={classes.assetSymbol}>{wallet.asset.code}</div>
                  {wallet.balance != "0" && < div className={classes.assetBalance}>{wallet.balance}</div>}
                </div>
              ))}
          </div>
        </div>

        {selectedWallet && (
          <div className={classes.assetDetailSection}>
            <div className={classes.assetName}>
              {selectedWallet.asset.name}
            </div>
            <div className={classes.assetMetricsRow}>
              <div>
                <div className={classes.metricLabel}>Available</div>
                <div className={classes.metricValue}>
                  {selectedWallet?.balance || `0`} {selectedSymbol}
                </div>
              </div>
              {selectedWallet.base_balance > 0 && (
                <div>
                  <div className={classes.metricLabel}>Value</div>
                  <div className={classes.metricValue}>
                    ${selectedWallet.base_balance}
                  </div>
                </div>
              )}
              {EARNING_RATES[selectedWallet?.asset.code] && (
                <div>
                  <div className={classes.metricLabel}>Earn</div>
                  <div className={classes.metricValue}>
                    {EARNING_RATES[selectedWallet?.asset.code]}
                  </div>
                </div>
              )}
            </div>
            <div className={classes.actionButtonsRow}>
              <button className={classes.actionBtn} onClick={handleTrade}>Trade</button>
              <button className={classes.lightBtn} onClick={handleDeposit}>Add funds</button>
              <button className={classes.lightBtn} onClick={handleWithdraw}>Withdraw</button>
            </div>
          </div>
        )}

        {selectedWallet && <RewardsBanner assetCode={selectedWallet.asset.code} assetIconUrl={selectedWallet.asset.icon_url} />}

        {selectedWallet && (
          <OffersSection
            assetCode={selectedWallet.asset.code}
            assetIconUrl={selectedWallet.asset.icon_url}
            wallets={wallets}
            onSelectWallet={(wallet: Wallet) => setSelectedWallet(wallet)}
          />
        )}

        {selectedWallet && distributionSchedule.length > 0 && <DistributionSchedule />}

        <div className={classes.assetName}>Activity</div>

        <div className={classes.txList}>
          {(isLoadingBalances || isLoadingLedger)
            ? [...Array(3)].map((_, i) => (
              <div key={i} className={`${classes.shimmer} ${classes.txRowShimmer}`} />
            ))
            : filteredLedgerEntries.map((tx) => (
              <div key={tx.id} className={classes.txRow}>
                <div>
                  {toCapitalizedWords(tx.category)}
                </div>
                <div className={tx.amount.startsWith('+') ? classes.txAmountPositive : classes.txAmountNegative}>
                  {tx.amount}
                </div>

                <div className={classes.txDate}>{formatDate(tx.createdAtIso)}</div>
              </div>
            ))}
        </div>
      </div>
    </div >
  );
};

export default WalletPage;
